import { Field, Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { ClientAccountEmailModel } from "services/api/cplapi/CPLAPI";
import { selectDefaultEmailAddress, setDefaultEmailAddress } from "./ClientEmailAddressesSlice";
import { selectGlobalSettings } from "app/modules/system/settings/ClientPortalSettingsSlice";

export type ClientEmailAddressDetailsProps = {
  ClientEmailAddressDetails: ClientAccountEmailModel;
  UpdateClientEmailAddressDetailsCallback(clientEmailAddressDetails: ClientAccountEmailModel): void;
  DeleteClientEmailAddressDetailsCallback(clientEmailAddressDetails: ClientAccountEmailModel): void;
};

const ClientEmailAddressDetails: React.FC<ClientEmailAddressDetailsProps> = ({
  ClientEmailAddressDetails,
  UpdateClientEmailAddressDetailsCallback,
  DeleteClientEmailAddressDetailsCallback,
}) => {
  const dispatch = useDispatch();
  const defaultEmail = useSelector(selectDefaultEmailAddress);
  const { showEmailAddressEdit } = useSelector(selectGlobalSettings);
  const canEdit = showEmailAddressEdit !== false;
  return (
    <Formik
      enableReinitialize={true}
      initialValues={ClientEmailAddressDetails}
      onSubmit={(newEmailAddressDetails) => UpdateClientEmailAddressDetailsCallback(newEmailAddressDetails)}
    >
      {({ values, setFieldValue, setFieldTouched, touched }) => (
        <Form>
          <div className="row m-1">
            <div className="col-sm-12 col-md-8">
              <label htmlFor={`${ClientEmailAddressDetails.clientEmailID}EmailAddress`} className="form-label required">
                Email Address
              </label>
              <Field
                data-test-id={`email-addresses-list__email-address-input-${ClientEmailAddressDetails.clientEmailID}`}
                name="emailAddress"
                type="email"
                className="form-control form-control-solid"
                disabled={!canEdit}
              />
            </div>
            <div className="col-sm-12 col-md-12 col-lg-4 pb-2 align-self-end">
              <Field
                data-test-id={`email-addresses-list__default-radio-${ClientEmailAddressDetails.clientEmailID}`}
                name="default"
                type="radio"
                className="form-check-input"
                onChange={(e) => {
                  setFieldValue("default", e.target.checked);
                  setFieldTouched("default", true);
                  dispatch(setDefaultEmailAddress(ClientEmailAddressDetails));
                }}
                checked={ClientEmailAddressDetails.clientEmailID === defaultEmail.clientEmailID}
              />
              <label htmlFor={`${ClientEmailAddressDetails.clientEmailID}IsDefault`} className="form-check-label ms-2">
                Default?
              </label>
            </div>
          </div>
          <div className="row m-1">
            <div className="col-12">
              <label htmlFor={`${ClientEmailAddressDetails.clientEmailID}EmailDescription`} className="form-label me-1">
                Description
              </label>
              <Field
                data-test-id={`email-addresses-list__description-input-${ClientEmailAddressDetails.clientEmailID}`}
                name="emailDescription"
                className="form-control form-control-solid"
                disabled={!canEdit}
              />
            </div>
          </div>
          <div className="row pt-1 m-1">
            <div className="col-sm-12 col-md-12 col-lg-4 align-self-center">
              <Field
                data-test-id={`email-addresses-list__used-for-invoices-checkbox-${ClientEmailAddressDetails.clientEmailID}`}
                name="usedForInvoice"
                type="checkbox"
                className="form-check-input"
              />
              <label
                htmlFor={`${ClientEmailAddressDetails.clientEmailID}UsedForInvoice`}
                className="form-check-label ms-2"
              >
                Used for invoices?
              </label>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-4 align-self-center">
              <Field
                data-test-id={`email-addresses-list__used-for-documents-checkbox-${ClientEmailAddressDetails.clientEmailID}`}
                name="usedForDocuments"
                type="checkbox"
                className="form-check-input"
              />
              <label
                htmlFor={`${ClientEmailAddressDetails.clientEmailID}UsedForDocuments`}
                className="form-check-label ms-2"
              >
                Used for documents?
              </label>
            </div>
            <div className="col-sm-12 col-md-12 col-lg-4 pe-2 text-end">
              <button
                data-test-id={`email-addresses-list__save-button-${ClientEmailAddressDetails.clientEmailID}`}
                className="cpl-btn cpl-btn-success me-1"
                type="submit"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                disabled={
                  !touched.emailAddress &&
                  !touched.emailDescription &&
                  !touched.usedForDocuments &&
                  !touched.usedForInvoice &&
                  !touched.default
                }
              >
                Save
              </button>
              {canEdit && (
                <button
                  data-test-id={`email-addresses-list__delete-button-${ClientEmailAddressDetails.clientEmailID}`}
                  className="cpl-btn cpl-btn-danger"
                  type="button"
                  onClick={() => DeleteClientEmailAddressDetailsCallback(values)}
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default ClientEmailAddressDetails;
